import type { ReactNode } from "react";
import type { TenantLabels } from "../../lib/tenantLabels";
import {
  CalendarIcon,
  DocumentIcon,
  HomeIcon,
  LinkIcon,
  LocationPinIcon,
  MessageIcon,
  PersonIcon,
  SettingsIcon,
  VideoIcon
} from "../icons/AppIcons";
import type { OperatorBottomNavigationItem } from "./OperatorBottomNavigation";

type PageLink = {
  to: string;
  label: string;
};

type ResolveOperatorBottomItemsOptions = {
  links: readonly PageLink[];
  tenantSlug: string;
  labels: TenantLabels;
  currentArea: "teacher" | "admin";
};

const ADMIN_HOME_LABEL = "管理者ホーム";

function resolveLinkTarget(linkTo: string, tenantSlug: string): string {
  return linkTo.replace(":tenantSlug", tenantSlug);
}

function isPageLocalExcludedDestination(path: string, currentArea: "teacher" | "admin"): boolean {
  return (
    /\/home$/.test(path)
    || /\/teacher$/.test(path)
    || (currentArea !== "admin" && /\/teacher\/admin$/.test(path))
    || /\/support$/.test(path)
  );
}

function resolveNavigationIcon(path: string): ReactNode {
  if (path.includes("/absence-request")) {
    return <CalendarIcon size={24} />;
  }

  if (path.includes("/recurring-slots")) {
    return <LocationPinIcon size={24} />;
  }

  if (path.includes("/students") || path.includes("/family-accounts") || path.includes("/guardian-link")) {
    return <PersonIcon size={24} />;
  }

  if (path.includes("/enrollments") || path.includes("/integrations")) {
    return <LinkIcon size={24} />;
  }

  if (path.includes("/lessons") || path.includes("/schedule") || path.includes("/events") || path.includes("/public-events")) {
    return <CalendarIcon size={24} />;
  }

  if (path.includes("/member-communications")) {
    return <MessageIcon size={24} />;
  }

  if (path.includes("/videos")) {
    return <VideoIcon size={24} />;
  }

  if (path.includes("/tenant") || path.includes("/public-settings")) {
    return <SettingsIcon size={24} />;
  }

  if (path.includes("/billing")) {
    return <DocumentIcon size={24} />;
  }

  return <DocumentIcon size={24} />;
}

function resolveActivePrefixes(path: string): string[] {
  if (path.includes("/absence-request")) {
    return [path.replace(/\/new$/, "")];
  }

  if (path.includes("/students")) {
    return [path];
  }

  if (path.includes("/guardian-links")) {
    return [path, path.replace(/\/guardian-links$/, "/guardian-link-requests")];
  }

  if (path.includes("/lessons")) {
    return [path];
  }

  if (path.includes("/schedule")) {
    return [
      path,
      path.replace(/\/schedule$/, "/schedule-months"),
      path.replace(/\/schedule$/, "/occurrences")
    ];
  }

  return [path];
}

function resolvePageLinkLabel(label: string, labels: TenantLabels): string {
  if (label === "講師ホームへ戻る") {
    return `${labels.teacher}ホーム`;
  }

  if (label === "管理ホームへ戻る") {
    return "管理ホーム";
  }

  if (label.endsWith("へ戻る")) {
    return label.slice(0, -"へ戻る".length);
  }

  return label;
}

export function resolveOperatorBottomItems(
  {
    links,
    tenantSlug,
    labels,
    currentArea
  }: ResolveOperatorBottomItemsOptions
): OperatorBottomNavigationItem[] {
  const seen = new Set<string>();
  const items: OperatorBottomNavigationItem[] = [];
  const adminHomeTo = `/t/${tenantSlug}/teacher/admin`;

  if (currentArea === "admin") {
    seen.add(adminHomeTo);
    items.push({
      key: "admin-home",
      label: ADMIN_HOME_LABEL,
      to: adminHomeTo,
      icon: <HomeIcon size={24} />,
      match: "exact"
    });
  }

  for (const link of links) {
    const to = resolveLinkTarget(link.to, tenantSlug);

    if (isPageLocalExcludedDestination(to, currentArea) || seen.has(to)) {
      continue;
    }

    seen.add(to);

    items.push({
      key: to,
      label: resolvePageLinkLabel(link.label, labels),
      to,
      icon: resolveNavigationIcon(to),
      match: "prefix",
      activePrefixes: resolveActivePrefixes(to)
    });
  }

  return items;
}
